import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    ExternalLink,
    RefreshCw,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    ExternalLink as NotionIcon
} from 'lucide-react';
import { ControlledToggleButton } from '@/components/ControlledToggleButton';
import { useNotionIntegration } from '@/hooks/useNotionIntegration';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { fetchSubscription, type Subscription } from '@/services/api';
import { NotionDisconnectDialog } from '@/components/NotionDisconnectDialog';

interface NotionIntegrationCardProps {
    className?: string;
    premiumUntil: string | null;
}

export const NotionIntegrationCard: React.FC<NotionIntegrationCardProps> = ({ className, premiumUntil }) => {
    const {
        status,
        isLoading,
        isConnecting,
        isDisconnecting,
        connect,
        toggleAutoSync,
        disconnect,
        formatDate,
        isConnected
    } = useNotionIntegration();

    // Check if user is on free plan
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [isFreePlan, setIsFreePlan] = useState<boolean>(false);
    const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

    useEffect(() => {
        console.log('NotionIntegrationCard - isFreePlan changed to:', isFreePlan);
    }, [isFreePlan]);

    // Fetch subscription data
    useEffect(() => {
        const loadSubscription = async () => {
            try {
                const subscriptionData = await fetchSubscription();
                console.log('NotionIntegrationCard - Subscription data:', subscriptionData);
                setSubscription(subscriptionData);
            } catch (error) {
                console.error('Failed to load subscription:', error);
                setSubscription(null);
            }
        };

        loadSubscription();
    }, []);

    // Determine if user is on free plan
    useEffect(() => {
        const activeSubscriptionStatuses = ['active', 'trialing'];

        const hasActiveSubscription = subscription && activeSubscriptionStatuses.includes(subscription.status);
        const isPremiumUntilValid = premiumUntil &&
            premiumUntil !== 'null' &&
            premiumUntil !== 'undefined' &&
            new Date(premiumUntil) > new Date();

        const isPremium = hasActiveSubscription && isPremiumUntilValid;
        setIsFreePlan(!isPremium);
    }, [subscription, premiumUntil]);

    if (isLoading) {
        return (
            <Card className={cn("w-full bg-gray-800 border-gray-700", className)}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-white">
                        <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                            <NotionIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        Notion Integration
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                        Loading integration status...
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="animate-pulse space-y-3">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                        <div className="h-8 bg-gray-700 rounded w-32"></div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!isConnected) {
        return (
            <Card className={cn("w-full max-w-md bg-gray-800 border-gray-700", className)}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-white">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                            <img
                                src="/assets/notion.svg"
                                alt="Notion Logo"
                                className="text-gray-400"
                            />


                        </div>
                        Notion Integration
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                        Connect your Notion workspace to automatically sync your social media posts.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Status */}
                    <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-400" />
                            <span className="text-sm font-medium text-gray-300">Not Connected</span>
                        </div>
                    </div>

                    {/* Free plan message */}
                    {(() => {
                        return isFreePlan ? (
                            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-yellow-200">
                                        Only <span className="font-semibold">10 posts</span> will get synced to Notion since you are on free plan.{' '}
                                        <Link
                                            to="/profile?active=subscription"
                                            className="underline hover:text-yellow-100 transition-colors"
                                        >
                                            Purchase subscription
                                        </Link> to sync all your posts.
                                    </p>
                                </div>
                            </div>
                        ) : null;
                    })()}

                    <Button
                        onClick={connect}
                        disabled={isConnecting}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                        {isConnecting ? (
                            <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Connecting...
                            </>
                        ) : (
                            <>
                                <NotionIcon className="h-4 w-4 mr-2" />
                                Connect Notion
                            </>
                        )}
                    </Button>
                    <div className="flex justify-center">
                        <a className="text-sm transition-colors" href="https://www.instavault.co/blog/veb847zufiy97s0s60vegg0i" target="_blank">View setup guide</a>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn("w-full max-w-md bg-gray-800 border-gray-700", className)}>
            <CardHeader>
                <CardTitle className="flex items-center gap-3 text-white">
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center">
                        <img
                            src="/assets/notion.svg"
                            alt="Notion Logo"
                            className="text-gray-400"
                        />
                    </div>
                    Notion Integration
                </CardTitle>

            </CardHeader>
            <CardContent className="space-y-4">
                {/* Connection Status */}
                <div className="flex items-center justify-between rounded-lg">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-400" />
                        <span className="text-sm font-medium text-gray-300">Connected</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-400 p-3 rounded-lg">
                        <Clock className="h-4 w-4" />
                        <span>Last sync: {formatDate(status?.last_sync_at || null)}</span>
                    </div>

                </div>

                {/* Last Sync Info */}

                {/* Auto-Sync Toggle */}
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h4 className="font-medium text-white">Auto-Sync</h4>
                            <p className="text-sm text-gray-400">
                                {status?.auto_sync_enabled
                                    ? 'New posts are automatically synced to your Notion database.'
                                    : 'Auto-sync is disabled. Enable to sync new posts automatically.'
                                }
                            </p>
                        </div>
                        <ControlledToggleButton
                            isOn={status?.auto_sync_enabled || false}
                            onToggle={toggleAutoSync}
                            disabled={isConnecting}
                        />
                    </div>
                </div>

                {(() => {
                    return isFreePlan ? (
                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-yellow-200">
                                    Only <span className="font-semibold">10 posts</span> will get synced to Notion since you are on free plan.{' '}
                                    <Link
                                        to="/profile?active=subscription"
                                        className="underline hover:text-yellow-100 transition-colors"
                                    >
                                        Purchase subscription
                                    </Link> to sync all your posts.
                                </p>
                            </div>
                        </div>
                    ) : null;
                })()}



                <div className="grid grid-cols-2 gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = '/notion/status'}
                        className="bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white"
                    >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Details
                    </Button>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDisconnectDialog(true)}
                        disabled={isDisconnecting}
                        className="bg-gray-700 border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                        {isDisconnecting ? (
                            <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Disconnecting...
                            </>
                        ) : (
                            <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Disconnect
                            </>
                        )}
                    </Button>
                </div>
            </CardContent>

            <NotionDisconnectDialog
                isOpen={showDisconnectDialog}
                onClose={() => setShowDisconnectDialog(false)}
                onConfirm={disconnect}
            />
        </Card>
    );
};